import { BUILTIN_SIGNAL_DEFINITIONS } from '../services/builtinSignalDefinitions.generated';

// Signal names and descriptions have one source of truth: BUILTIN_CLASSIFIERS in
// platform-services/lumoz-contracts. The console reaches it two ways. Normally
// through /v2/data/classifiers/definitions, which also carries a tenant's custom
// signals. But a trace table cell rendering a badge cannot await a fetch, so it
// falls back to the generated mirror of the same contract, which CI keeps fresh.
//
// These maps used to be hand-written and had drifted badly: nine entries named
// signals the product had retired, and three shipped signals were missing
// outright, so data_exfiltration, emotion and rag_quality rendered as
// title-cased keys.

// Signals the product no longer emits, kept because traces that predate the
// change still carry them and would otherwise render as raw keys: pii_leakage
// would read "Pii Leakage" rather than "Data Leak". Last written 2026-08-09.
// Nothing belongs here that the contract defines.
const RETIRED_SIGNAL_LABELS = {
  faithfulness: 'Ungrounded',
  pii_leakage: 'Data Leak',
  misuse: 'Jailbreaking',
  answer_relevancy: 'Irrelevant',
  contextual_relevancy: 'Wrong Context',
};

const RETIRED_SIGNAL_DESCRIPTIONS = {
  faithfulness: "The agent's response contains claims not supported by the information it was given.",
  pii_leakage: "Sensitive personal information or secrets were exposed in the agent's output.",
  misuse: "Someone attempted to manipulate or hijack the agent's instructions.",
  answer_relevancy: "The agent's response did not address what the user asked.",
  contextual_relevancy: "The information retrieved to answer the user's query was not relevant.",
};

const SIGNAL_LABELS = BUILTIN_SIGNAL_DEFINITIONS.reduce(
  (acc, d) => ({ ...acc, [d.classifier_key]: d.name }),
  { ...RETIRED_SIGNAL_LABELS },
);

const SIGNAL_DESCRIPTIONS = BUILTIN_SIGNAL_DEFINITIONS.reduce(
  (acc, d) => ({ ...acc, [d.classifier_key]: d.description }),
  { ...RETIRED_SIGNAL_DESCRIPTIONS },
);

// A subtype's name, by its key. The contract owns these too, and without them a
// subtype rendered as its title-cased key: workflow_anomaly read "Workflow
// Anomaly", the signal's own former name, long after it was renamed.
//
// Keyed by signal first, because a subtype key is only unique WITHIN a signal.
// tool_parameter_error is defined by both error_detection ("Tool Parameter") and
// tool_correctness ("Tool Parameter Error"), so a flat map has to pick one and
// would be wrong half the time.
const SUBTYPE_LABELS_BY_SIGNAL = BUILTIN_SIGNAL_DEFINITIONS.reduce((acc, d) => {
  acc[d.classifier_key] = (d.subtypes || []).reduce(
    (inner, subtype) => (subtype.key ? { ...inner, [subtype.key]: subtype.name } : inner),
    {},
  );
  return acc;
}, {});

// Event keys sit one level below subtypes and the contract names all 55 of them,
// so the same column that renders a subtype renders these: without them
// http_other read "Http Other" rather than "Other Status". Flat, because an
// event key carries its subtype's signal in its own prefix.
const EVENT_KEY_LABELS = BUILTIN_SIGNAL_DEFINITIONS.reduce((acc, d) => {
  (d.subtypes || []).forEach((subtype) => {
    (subtype.event_keys || []).forEach((event) => {
      if (event.key && !acc[event.key]) acc[event.key] = event.name;
    });
  });
  return acc;
}, {});

const SIGNAL_LABEL_KEY_LOOKUP = Object.entries(SIGNAL_LABELS).reduce((acc, [key, label]) => {
  const normalizedLabel = typeof label === 'string' ? label.trim().toLowerCase() : '';
  if (!normalizedLabel) return acc;
  if (!acc[normalizedLabel]) acc[normalizedLabel] = key;
  return acc;
}, {});

// Polarity comes from the contract. No built-in is positive today, so this is
// empty; it stays derived rather than hardcoded so a positive signal works the
// day one ships.
const POSITIVE_SIGNAL_KEYS = new Set(
  BUILTIN_SIGNAL_DEFINITIONS.filter(d => d.polarity === 'positive').map(d => d.classifier_key),
);

const toTitleCase = (value) =>
  value
    .split(' ')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const normalizeSignalKey = (key) => {
  if (typeof key !== 'string') return null;
  const trimmed = key.trim().toLowerCase();
  return trimmed || null;
};

export const resolveSignalFilterKey = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const labelMatch = SIGNAL_LABEL_KEY_LOOKUP[trimmed.toLowerCase()];
  if (labelMatch) return labelMatch;

  return normalizeSignalKey(trimmed.replace(/\s+/g, '_'));
};

export const getSignalFilterDisplayLabel = (value) => {
  const resolvedKey = resolveSignalFilterKey(value);
  if (resolvedKey) return formatSignalLabelFromKey(resolvedKey) || resolvedKey;

  const normalized = normalizeSignalKey(value);
  if (!normalized) return null;
  return formatSignalLabelFromKey(normalized) || normalized;
};

export const formatSignalLabelFromKey = (key) => {
  const normalized = normalizeSignalKey(key);
  if (!normalized) return null;
  return SIGNAL_LABELS[normalized] || toTitleCase(normalized.replace(/_/g, ' '));
};

/**
 * A subtype's display name, falling back to a title-cased key.
 *
 * The fallback is for a tenant's own custom subtypes and for dynamic event keys,
 * which the contract cannot know. It is not a substitute for a name the contract
 * does define.
 */
export const formatSubtypeLabel = (value, classifierKey = null) => {
  const normalized = normalizeSignalKey(value);
  if (!normalized) return '';

  const signal = normalizeSignalKey(classifierKey);
  if (signal && SUBTYPE_LABELS_BY_SIGNAL[signal]?.[normalized]) {
    return SUBTYPE_LABELS_BY_SIGNAL[signal][normalized];
  }

  // No signal in hand: take the first that defines this subtype. Ambiguous only
  // for tool_parameter_error, where both names say the same thing.
  for (const subtypes of Object.values(SUBTYPE_LABELS_BY_SIGNAL)) {
    if (subtypes[normalized]) return subtypes[normalized];
  }
  if (EVENT_KEY_LABELS[normalized]) return EVENT_KEY_LABELS[normalized];
  return toTitleCase(normalized.replace(/_/g, ' '));
};

export const getSignalDescriptionFromKey = (key) => {
  const normalized = normalizeSignalKey(key);
  if (!normalized) return '';
  return SIGNAL_DESCRIPTIONS[normalized] || '';
};

export const getSignalVariant = (polarity, classifierKey = null) => {
  const normalizedPolarity = typeof polarity === 'string' ? polarity.toLowerCase() : '';
  if (normalizedPolarity === 'positive') return 'info';
  if (normalizedPolarity === 'negative') return 'danger';

  const normalizedKey = normalizeSignalKey(classifierKey);
  if (normalizedKey && POSITIVE_SIGNAL_KEYS.has(normalizedKey)) return 'info';
  if (normalizedKey) return 'danger';
  return 'neutral';
};

export const getDetectionLabel = (detection) => {
  if (!detection || typeof detection !== 'object') return null;

  if (typeof detection.name === 'string' && detection.name.trim()) {
    return detection.name.trim();
  }

  const classifierKey = detection.classifier_key || detection.metric;
  return formatSignalLabelFromKey(classifierKey);
};

const getDetectionKey = (detection) => detection?.classifier_key || detection?.metric || null;

const pushUniqueBadge = (badges, seenLabels, label, variant) => {
  if (typeof label !== 'string') return;
  const trimmed = label.trim();
  if (!trimmed) return;

  const normalized = trimmed.toLowerCase();
  if (seenLabels.has(normalized)) return;
  seenLabels.add(normalized);
  badges.push({ label: trimmed, variant });
};

const getTraceSignalEntries = (trace) => {
  if (!trace || typeof trace !== 'object') return [];

  // Preferred source: full detections from /traces endpoint (can include repeats across spans).
  const detections = Array.isArray(trace.detections) ? trace.detections : [];
  if (detections.length > 0) {
    return detections
      .map((detection) => ({
        label: getDetectionLabel(detection),
        variant: getSignalVariant(detection?.polarity, getDetectionKey(detection)),
      }))
      .filter(entry => entry.label);
  }

  // Secondary source: classifier objects from issues/classify endpoints.
  const detectedClassifiers = Array.isArray(trace.detected_classifiers) ? trace.detected_classifiers : [];
  if (detectedClassifiers.length > 0) {
    return detectedClassifiers
      .map((classifier) => {
        if (typeof classifier === 'string') {
          return {
            label: formatSignalLabelFromKey(classifier),
            variant: getSignalVariant(null, classifier),
          };
        }
        return {
          label: getDetectionLabel(classifier),
          variant: getSignalVariant(classifier?.polarity, getDetectionKey(classifier)),
        };
      })
      .filter(entry => entry.label);
  }

  // Problems endpoint source: classifier keys in problem_types.
  const problemTypes = Array.isArray(trace.problem_types) ? trace.problem_types : [];
  if (problemTypes.length > 0) {
    return problemTypes
      .map((signalKey) => ({
        label: formatSignalLabelFromKey(signalKey),
        variant: getSignalVariant(null, signalKey),
      }))
      .filter(entry => entry.label);
  }

  // Legacy source: key arrays.
  const legacySignals = [
    ...(Array.isArray(trace.signal) ? trace.signal : []),
    ...(Array.isArray(trace.signals) ? trace.signals : []),
  ];
  if (legacySignals.length > 0) {
    return legacySignals
      .map((signalKey) => ({
        label: formatSignalLabelFromKey(signalKey),
        variant: getSignalVariant(null, signalKey),
      }))
      .filter(entry => entry.label);
  }

  // Safety fallbacks when only boolean flags are present.
  const fallback = [];
  if (trace.has_prompt_injection) {
    fallback.push({ label: 'Jailbreaking', variant: 'danger' });
  }
  if (trace.has_data_exfiltration) {
    fallback.push({ label: 'Data Leak', variant: 'danger' });
  }
  if (fallback.length > 0) return fallback;

  // Last-resort metric fallback.
  if (trace.worst_eval?.metric) {
    const metric = trace.worst_eval.metric;
    const label = formatSignalLabelFromKey(metric);
    if (label) {
      return [{ label, variant: getSignalVariant(trace.worst_eval?.polarity, metric) }];
    }
  }

  return [];
};

export const summarizeDetectionNames = (detections, maxNames = 2) => {
  const source = Array.isArray(detections) ? detections : [];
  const names = [...new Set(source.map(getDetectionLabel).filter(Boolean))];
  if (names.length === 0) return null;

  const visible = names.slice(0, maxNames).join(', ');
  const remaining = names.length - maxNames;
  return remaining > 0 ? `${visible} +${remaining}` : visible;
};

export const getTraceSignalBadges = (trace) => {
  const badges = [];
  if (!trace) return badges;
  const seen = new Set();

  getTraceSignalEntries(trace).forEach((entry) => {
    pushUniqueBadge(badges, seen, entry.label, entry.variant);
  });

  if (trace.errors > 0 || trace.error_count > 0) {
    badges.push({ label: `Errors ${trace.errors || trace.error_count}`, variant: 'danger' });
  }

  if (trace.tool_status?.issues > 0) {
    badges.push({ label: `Tool Issues ${trace.tool_status.issues}`, variant: 'warning' });
  }

  return badges;
};

export const getTraceSignalCounts = (trace) => {
  const entries = getTraceSignalEntries(trace);

  const counts = new Map();
  entries.forEach(({ label, variant }) => {
    const key = label.toLowerCase();
    if (!counts.has(key)) {
      counts.set(key, { label, count: 0, variant });
    }
    counts.get(key).count += 1;
  });

  const errorCount = trace?.errors || trace?.error_count || trace?.error_spans || 0;
  // Only add raw error count pill when no error_detection classifier already fired
  // (classifier produces label "Errors" → key "errors"; count pill "Errors N" → key "errors N" — different keys, both would render)
  if (errorCount > 0 && !counts.has('errors')) {
    const label = `Errors ${errorCount}`;
    counts.set(label.toLowerCase(), { label, count: 1, variant: 'danger' });
  }

  if (trace?.tool_status?.issues > 0 && !counts.has('tool issues')) {
    const label = `Tool Issues ${trace.tool_status.issues}`;
    counts.set(label.toLowerCase(), { label, count: 1, variant: 'warning' });
  }

  return [...counts.values()];
};

/**
 * How many signals a type detects.
 *
 * A type that declares no signals of its own detects exactly one thing: itself.
 * That is every custom signal, and counting only the declared ones left the
 * Custom group of the catalog reading "1 type" and no signals at all.
 *
 * The catalog's Signals column and its group header both read this, so the
 * column can always be summed down to the header above it.
 */
export const signalsDetected = (signal) => Math.max(
  1,
  Array.isArray(signal?.subtypes) ? signal.subtypes.length : 0,
);
